import {
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Switch,
  Text,
  Tooltip,
} from "components/ui";
import { useEffect, useState } from "react";
import {
  MdAdd,
  MdExpandMore,
  MdChevronRight,
  MdFolder,
  MdFolderOpen,
  MdPublic,
} from "react-icons/md";
import { knowledgeBaseService } from "services/knowledgeBaseService";
import {
  KbDocumentDto,
  KbFolderTreeDto,
  KbViewMode,
  KbWebSourceDto,
} from "types/knowledgeBase";
import DocumentsTable from "./DocumentsTable";
import WebSourcesTable from "./WebSourcesTable";

interface FolderGroupViewProps {
  folderTree: KbFolderTreeDto[];
  viewMode: KbViewMode;
  onFolderNavigate: (folderId: string) => void;
  onDelete: (docId: string) => void;
  onRefreshStatus: (docId: string) => void;
  onDocumentUpdated: (doc: KbDocumentDto) => void;
  onEditWebSource: (ws: KbWebSourceDto) => void;
  onDeleteWebSource: (id: string) => void;
  onSyncWebSource: (id: string) => void;
  onWebSourceUpdated: (ws: KbWebSourceDto) => void;
  onMove: (
    type: "document" | "webSource",
    id: string,
    folderId: string | null,
  ) => Promise<void>;
  onSetFolderGlobal: (id: string, isGlobal: boolean) => Promise<void>;
  onAddToKb: () => void;
  folders: Array<{ id: string; name: string; parentName?: string }>;
  refreshToken?: number;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onFolderToggleAll?: (docIds: string[], checked: boolean) => void;
  selectedWsIds?: Set<string>;
  onToggleWsSelect?: (id: string) => void;
  onFolderWsToggleAll?: (wsIds: string[], checked: boolean) => void;
}

interface FolderSectionState {
  expanded: boolean;
  documents: KbDocumentDto[];
  webSources: KbWebSourceDto[];
  loading: boolean;
  loaded: boolean;
}

const FolderSection = ({
  folder,
  viewMode,
  onFolderNavigate,
  onDelete,
  onRefreshStatus,
  onDocumentUpdated,
  onEditWebSource,
  onDeleteWebSource,
  onSyncWebSource,
  onWebSourceUpdated,
  onMove,
  onSetGlobal,
  folders,
  refreshToken,
  selectedIds,
  onToggleSelect,
  onFolderToggleAll,
  selectedWsIds,
  onToggleWsSelect,
  onFolderWsToggleAll,
}: {
  folder: KbFolderTreeDto;
  viewMode: KbViewMode;
  onFolderNavigate: (id: string) => void;
  onDelete: (id: string) => void;
  onRefreshStatus: (id: string) => void;
  onDocumentUpdated: (doc: KbDocumentDto) => void;
  onEditWebSource: (ws: KbWebSourceDto) => void;
  onDeleteWebSource: (id: string) => void;
  onSyncWebSource: (id: string) => void;
  onWebSourceUpdated: (ws: KbWebSourceDto) => void;
  onMove: (
    type: "document" | "webSource",
    id: string,
    folderId: string | null,
  ) => Promise<void>;
  onSetGlobal: (id: string, isGlobal: boolean) => Promise<void>;
  folders: Array<{ id: string; name: string; parentName?: string }>;
  refreshToken?: number;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onFolderToggleAll?: (docIds: string[], checked: boolean) => void;
  selectedWsIds?: Set<string>;
  onToggleWsSelect?: (id: string) => void;
  onFolderWsToggleAll?: (wsIds: string[], checked: boolean) => void;
}) => {
  const [state, setState] = useState<FolderSectionState>({
    expanded: false,
    documents: [],
    webSources: [],
    loading: false,
    loaded: false,
  });
  const [isDragOver, setIsDragOver] = useState(false);

  // Use live local counts once loaded, fall back to prop until then
  const itemCount = state.loaded
    ? state.documents.length + state.webSources.length
    : folder.documentCount + folder.webSourceCount;

  const loadContents = async () => {
    const [docs, ws] = await Promise.all([
      viewMode !== "webSources"
        ? knowledgeBaseService
            .getDocuments({ folderId: folder.id, pageSize: 100 })
            .then((r) => r.items)
        : Promise.resolve([] as KbDocumentDto[]),
      viewMode !== "documents"
        ? knowledgeBaseService.getWebSources(folder.id)
        : Promise.resolve([] as KbWebSourceDto[]),
    ]);
    setState((s) => ({
      ...s,
      documents: docs,
      webSources: ws,
      loaded: true,
      loading: false,
    }));
  };

  // Reload contents when parent signals an external mutation (global toggle, move, etc.)
  useEffect(() => {
    if (refreshToken !== undefined && refreshToken > 0 && state.loaded) {
      loadContents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const handleExpand = async () => {
    if (!state.expanded && !state.loaded) {
      setState((s) => ({ ...s, loading: true }));
      try {
        await loadContents();
        setState((s) => ({ ...s, expanded: true }));
      } catch {
        setState((s) => ({ ...s, loading: false, expanded: true }));
      }
    } else {
      setState((s) => ({ ...s, expanded: !s.expanded }));
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const type = e.dataTransfer.getData("itemType");
    const id = e.dataTransfer.getData("itemId");
    if (!id) return;
    await onMove(type as "document" | "webSource", id, folder.id);
    // Reload and expand so the dropped item appears immediately
    setState((s) => ({ ...s, loading: true }));
    try {
      await loadContents();
      setState((s) => ({ ...s, expanded: true }));
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  return (
    <Box mb={2}>
      <Card.Root>
        <Card.Body p={0}>
          {/* Folder header */}
          <HStack
            px={4}
            py={3}
            cursor="pointer"
            bg={isDragOver ? { base: "blue.50", _dark: "blue.900" } : undefined}
            borderRadius="md"
            outline={isDragOver ? "2px dashed" : undefined}
            outlineColor={isDragOver ? "blue.400" : undefined}
            _hover={{
              bg: isDragOver
                ? { base: "blue.50", _dark: "blue.900" }
                : { base: "gray.50", _dark: "gray.800" },
            }}
            transition="background 0.15s"
            onClick={handleExpand}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node))
                setIsDragOver(false);
            }}
            onDrop={handleDrop}
          >
            <Box
              color={{
                base: state.expanded ? "blue.500" : "gray.400",
                _dark: state.expanded ? "blue.400" : "gray.500",
              }}
            >
              {state.expanded ? (
                <MdExpandMore size={18} />
              ) : (
                <MdChevronRight size={18} />
              )}
            </Box>
            <Box color={{ base: "yellow.500", _dark: "yellow.400" }}>
              {state.expanded ? (
                <MdFolderOpen size={18} />
              ) : (
                <MdFolder size={18} />
              )}
            </Box>
            <Text
              fontWeight="semibold"
              fontSize="sm"
              flex={1}
              cursor="pointer"
              onClick={(e) => {
                e.stopPropagation();
                onFolderNavigate(folder.id);
              }}
              _hover={{ color: "blue.500" }}
            >
              {folder.name}
            </Text>
            <Tooltip
              content={
                folder.isGlobal
                  ? "Global — included in all widgets. Click to remove."
                  : "Make global — include in all widgets"
              }
            >
              <HStack gap={1} onClick={(e) => e.stopPropagation()}>
                {folder.isGlobal && (
                  <MdPublic size={14} color="var(--chakra-colors-purple-500)" />
                )}
                <Switch
                  size="sm"
                  colorPalette="purple"
                  checked={folder.isGlobal}
                  onCheckedChange={() =>
                    onSetGlobal(folder.id, !folder.isGlobal)
                  }
                />
              </HStack>
            </Tooltip>
            <Text fontSize="xs" color={{ base: "gray.500", _dark: "gray.400" }}>
              {itemCount} item{itemCount !== 1 ? "s" : ""}
            </Text>
          </HStack>

          {/* Expanded content — also a drop target */}
          {state.expanded && (
            <Box
              px={4}
              pb={4}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node))
                  setIsDragOver(false);
              }}
              onDrop={handleDrop}
              outline={isDragOver ? "2px dashed" : undefined}
              outlineColor={isDragOver ? "blue.400" : undefined}
              borderRadius="md"
            >
              {state.loading ? (
                <Text
                  fontSize="sm"
                  color={{ base: "gray.500", _dark: "gray.400" }}
                  py={2}
                >
                  Loading…
                </Text>
              ) : (
                <>
                  {/* Documents */}
                  {viewMode !== "webSources" && state.documents.length > 0 && (
                    <Box mb={state.webSources.length > 0 ? 3 : 0}>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                        letterSpacing="wide"
                        color={{ base: "gray.500", _dark: "gray.400" }}
                        mb={2}
                      >
                        Documents ({state.documents.length})
                      </Text>
                      <DocumentsTable
                        documents={state.documents}
                        onDelete={(id) => {
                          onDelete(id);
                          setState((s) => ({
                            ...s,
                            documents: s.documents.filter((d) => d.id !== id),
                          }));
                        }}
                        onRefreshStatus={async (id) => {
                          await onRefreshStatus(id);
                          if (state.loaded) await loadContents();
                        }}
                        onDocumentUpdated={(doc) => {
                          onDocumentUpdated(doc);
                          setState((s) => ({
                            ...s,
                            documents: s.documents.map((d) =>
                              d.id === doc.id ? doc : d,
                            ),
                          }));
                        }}
                        onMove={(id, fid) => onMove("document", id, fid)}
                        folders={folders}
                        selectedIds={selectedIds}
                        onToggleSelect={onToggleSelect}
                        onToggleAll={
                          onFolderToggleAll
                            ? (checked) =>
                                onFolderToggleAll(
                                  state.documents.map((d) => d.id),
                                  checked,
                                )
                            : undefined
                        }
                      />
                    </Box>
                  )}

                  {/* Web Sources */}
                  {viewMode !== "documents" && state.webSources.length > 0 && (
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                        letterSpacing="wide"
                        color={{ base: "gray.500", _dark: "gray.400" }}
                        mb={2}
                      >
                        Web Sources ({state.webSources.length})
                      </Text>
                      <WebSourcesTable
                        webSources={state.webSources}
                        onEdit={onEditWebSource}
                        onDelete={(id) => {
                          onDeleteWebSource(id);
                          setState((s) => ({
                            ...s,
                            webSources: s.webSources.filter((w) => w.id !== id),
                          }));
                        }}
                        onSync={onSyncWebSource}
                        onWebSourceUpdated={(ws) => {
                          onWebSourceUpdated(ws);
                          setState((s) => ({
                            ...s,
                            webSources: s.webSources.map((w) =>
                              w.id === ws.id ? ws : w,
                            ),
                          }));
                        }}
                        onMove={(id, fid) => onMove("webSource", id, fid)}
                        folders={folders}
                        selectedIds={selectedWsIds}
                        onToggleSelect={onToggleWsSelect}
                        onToggleAll={
                          onFolderWsToggleAll
                            ? (checked) =>
                                onFolderWsToggleAll(
                                  state.webSources.map((ws) => ws.id),
                                  checked,
                                )
                            : undefined
                        }
                      />
                    </Box>
                  )}

                  {state.documents.length === 0 &&
                    state.webSources.length === 0 && (
                      <Text
                        fontSize="sm"
                        color={{ base: "gray.500", _dark: "gray.400" }}
                        py={2}
                      >
                        This folder is empty.
                      </Text>
                    )}
                </>
              )}
            </Box>
          )}
        </Card.Body>
      </Card.Root>
    </Box>
  );
};

const FolderGroupView = ({
  folderTree,
  viewMode,
  onFolderNavigate,
  onDelete,
  onRefreshStatus,
  onDocumentUpdated,
  onEditWebSource,
  onDeleteWebSource,
  onSyncWebSource,
  onWebSourceUpdated,
  onMove,
  onSetFolderGlobal,
  onAddToKb,
  folders,
  refreshToken,
  selectedIds,
  onToggleSelect,
  onFolderToggleAll,
  selectedWsIds,
  onToggleWsSelect,
  onFolderWsToggleAll,
}: FolderGroupViewProps) => {
  if (folderTree.length === 0) {
    return (
      <Card.Root>
        <Card.Body>
          <Flex direction="column" align="center" gap={3} py={8}>
            <MdFolder size={40} color="gray" />
            <Text color={{ base: "gray.500", _dark: "gray.400" }}>
              No folders yet. Create folders in the sidebar to organize your
              knowledge base.
            </Text>
            <Button size="sm" colorPalette="blue" onClick={onAddToKb}>
              <MdAdd /> Add to KB
            </Button>
          </Flex>
        </Card.Body>
      </Card.Root>
    );
  }

  return (
    <Box>
      {folderTree.map((folder) => (
        <FolderSection
          key={folder.id}
          folder={folder}
          viewMode={viewMode}
          onFolderNavigate={onFolderNavigate}
          onDelete={onDelete}
          onRefreshStatus={onRefreshStatus}
          onDocumentUpdated={onDocumentUpdated}
          onEditWebSource={onEditWebSource}
          onDeleteWebSource={onDeleteWebSource}
          onSyncWebSource={onSyncWebSource}
          onWebSourceUpdated={onWebSourceUpdated}
          onMove={onMove}
          onSetGlobal={onSetFolderGlobal}
          folders={folders}
          refreshToken={refreshToken}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          onFolderToggleAll={onFolderToggleAll}
          selectedWsIds={selectedWsIds}
          onToggleWsSelect={onToggleWsSelect}
          onFolderWsToggleAll={onFolderWsToggleAll}
        />
      ))}
    </Box>
  );
};

export default FolderGroupView;
